import { Resume } from '@sr/types';
import { useState } from 'react';
import { Box, Button, Text } from '../../ui';
import MessageContainer from './MessageContainer';
import trackEvent from 'utils/analytics';
import { Sentry } from 'utils/sentry';
import API from 'v1/utils/api';

type Status =
  | 'initial'
  | 'loading'
  | 'complete'
  | 'complete-after-timeout'
  | 'error';

export default function ({ resume }: { resume: Resume }) {
  const [status, setStatus] = useState<Status>('initial');
  const needsUpgrade = !API.resume.isUsingMarkdown(resume);

  async function upgrade() {
    try {
      await API.resume.upgradeToMarkdown(resume);
      setStatus('complete');
      trackEvent.upgradedToMarkdown(resume.id);
      setTimeout(() => setStatus('complete-after-timeout'), 5000);
    } catch (error) {
      Sentry.captureException(error, {
        tags: { resumeId: resume.id },
      });
      setStatus('error');
    }
  }

  if (needsUpgrade && status === 'initial') {
    return (
      <MessageContainer>
        <Text variant="titleEmphasis" pt={2} pb={4}>
          New editor available
        </Text>
        <Box sx={{ mb: 5 }}>
          Upgrade your resume to take advantage of our new rich text editor. It
          supports bullets, lists, bold, italic, links, and more.
        </Box>
        <Button onClick={upgrade}>Upgrade Resume</Button>
      </MessageContainer>
    );
  } else if (status === 'loading') {
    return (
      <MessageContainer>
        <Text variant="titleEmphasis" pt={2} pb={4}>
          Upgrading...
        </Text>
      </MessageContainer>
    );
  } else if (status === 'complete') {
    return (
      <MessageContainer>
        <Text variant="titleEmphasis" pt={2} pb={4}>
          Upgrade complete
        </Text>
        <Box sx={{ mb: 5 }}>
          Enjoy adding bullets, lists, bold, italic, links, and more to your
          resume.
        </Box>
      </MessageContainer>
    );
  } else if (status === 'error') {
    return (
      <MessageContainer color="rgba(255, 0, 0, 0.1)">
        <Text variant="titleEmphasis" pt={2} pb={4}>
          Something went wrong
        </Text>
        <Box sx={{ mb: 5 }}>
          Check the main menu for the backup copy of your resume we created.
        </Box>
      </MessageContainer>
    );
  } else {
    return null;
  }
}
